import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { localizeNotificationParams } from './notificationParams';
import { useSignalRConnection } from '../../hooks/useSignalRConnection';
import { INCREMENT_NOTIFICATIONS_UNREAD_COUNT } from '../../store/redux/actions/notificationActions';
import { markNotificationAsRead } from '../../api/General/Notification';
import { notifyPendingActionsChanged } from '../pendingActions/usePendingActions';
import { notificationIconMap } from './notificationIconMap';
import Icon from '../AppIcon';
import { cn } from '../../utils/cn';
import { createUrl } from '../../utils';

interface PopupNotification {
  id: number;
  title?: string;
  titleParams?: string;
  body?: string;
  bodyParams?: string;
  notificationUrl?: string;
  notificationTypeName?: string;
  createdOn: string;
  popupId: number;
  /** Set when the notification is about something waiting on this user. */
  actionSource?: string | null;
  actionEntityId?: string | null;
}

const MAX_POPUPS = 5;
const AUTO_DISMISS_MS = 30_000;

const NotificationPopup: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { subscribe } = useSignalRConnection();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const companyPrimaryColor = useSelector((state: any) => state.user?.companyPrimaryColor);
  const nextPopupIdRef = useRef(0);

  const [popups, setPopups] = useState<PopupNotification[]>([]);
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((popupId: number) => {
    setPopups(prev => prev.filter(p => p.popupId !== popupId));
    const timer = timersRef.current.get(popupId);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(popupId);
    }
  }, []);

  useEffect(() => {
    const unsub = subscribe('NotificationReceived', (data: any) => {
      dispatch({ type: INCREMENT_NOTIFICATIONS_UNREAD_COUNT });

      const popupId = nextPopupIdRef.current++;
      const popup: PopupNotification = {
        id: data.id,
        title: data.title,
        titleParams: data.titleParams,
        body: data.body,
        bodyParams: data.bodyParams,
        notificationUrl: data.notificationUrl,
        notificationTypeName: data.notificationTypeName,
        createdOn: data.createdOn,
        popupId,
        actionSource: data.actionSource ?? null,
        actionEntityId: data.actionEntityId ?? null,
      };

      // Something new is waiting on this user: move the top-bar badge now rather
      // than up to a minute later on the next poll. The popup itself stays
      // read-only — it auto-dismisses after 30s, which is no place for a
      // decision that may need a rejection reason.
      if (popup.actionSource) notifyPendingActionsChanged();

      // Auto-dismiss after 30s so AFK users don't silently lose popups
      const timer = setTimeout(() => {
        setPopups(prev => prev.filter(p => p.popupId !== popupId));
        timersRef.current.delete(popupId);
      }, AUTO_DISMISS_MS);
      timersRef.current.set(popupId, timer);

      setPopups(prev => {
        const next = [popup, ...prev];
        // If we're pushing out an old popup, clear its timer
        if (next.length > MAX_POPUPS) {
          const removed = next.slice(MAX_POPUPS);
          removed.forEach(p => {
            const t = timersRef.current.get(p.popupId);
            if (t) {
              clearTimeout(t);
              timersRef.current.delete(p.popupId);
            }
          });
        }
        return next.slice(0, MAX_POPUPS);
      });
    });

    return () => {
      unsub();
      // Clean up all timers on unmount
      timersRef.current.forEach(timer => clearTimeout(timer));
      timersRef.current.clear();
    };
  }, [subscribe, dispatch]);

  const handleClick = useCallback(async (popup: PopupNotification) => {
    // The SAME parser the bell list uses. The hand-rolled copy that used to live here read
    // `new URL(...).pathname` and threw the query string away, so every parameterised deep link
    // (CO-583's overtime approvals link carries userId/shiftId/from/to) landed the reviewer on an
    // unfiltered page — the popup and the bell disagreed about where one notification points.
    const url = createUrl(popup);

    if (url) {
      try { await markNotificationAsRead(popup.id); } catch { /* ignore */ }
      navigate(url);
    }

    dismiss(popup.popupId);
  }, [navigate, dismiss]);

  if (popups.length === 0) return null;

  return (
    <div
      className="fixed top-20 end-4 z-[101] flex flex-col gap-2 w-full max-w-[400px] pointer-events-none"
      data-testid="notification-popup-container"
    >
      {popups.map((popup) => {
        const iconName = notificationIconMap[popup.notificationTypeName || ''] || 'Bell';
        const hasAction = !!popup.notificationUrl;

        return (
          <div
            key={popup.popupId}
            data-testid="notification-popup-card"
            className={cn(
              'pointer-events-auto bg-card border border-border rounded-xl shadow-lg p-4 w-full',
              'animate-in slide-in-from-top-4 duration-300',
              // `hover:bg-accent/50` painted this toast half amber on hover: `--color-accent` is
              // #f59e0b, the same value as `--color-warning`, so the shadcn "subtle surface" role
              // is the warning colour in this codebase. A routine informational toast must not
              // read as an alert. Primary at 5% is the restrained equivalent and tracks the
              // tenant's own colour.
              hasAction && 'cursor-pointer hover:bg-primary/5 transition-colors'
            )}
            onClick={hasAction ? () => handleClick(popup) : undefined}
            role={hasAction ? 'button' : undefined}
            tabIndex={hasAction ? 0 : undefined}
            onKeyDown={hasAction ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(popup); } : undefined}
          >
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${companyPrimaryColor || 'var(--primary)'}15` }}
              >
                <Icon
                  name={iconName}
                  size={20}
                  style={{ color: companyPrimaryColor || 'var(--primary)' }}
                />
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground leading-tight truncate">
                  {t(popup.title ?? '', { ...localizeNotificationParams(popup.titleParams, t, i18n), nsSeparator: false })}
                </p>
                {popup.body && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                    {t(popup.body, { ...localizeNotificationParams(popup.bodyParams, t, i18n), nsSeparator: false })}
                  </p>
                )}
                {hasAction && (
                  <p className="text-xs mt-1.5 font-medium" style={{ color: companyPrimaryColor || 'var(--primary)' }}>
                    {popup.actionSource ? t('Action required') : t('notifications.popup.viewDetails', 'View details')}
                  </p>
                )}
              </div>

              {/* Close button */}
              <button
                data-testid="notification-popup-close"
                onClick={(e) => {
                  e.stopPropagation();
                  dismiss(popup.popupId);
                }}
                className="p-1 rounded hover:bg-muted transition-colors flex-shrink-0"
                aria-label={t('notifications.popup.close', 'Close')}
              >
                <Icon name="X" size={14} className="text-muted-foreground" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default NotificationPopup;
